import {
  ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQuery } from "react-query";

import {
  BasePagination,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import { getGroupList, getGroupPrevilegeGroupList } from "api/uGroupAPI";
import React from "react";
import { Checkbox, TableColumnsType } from "antd";
import { matchStringSearchKeyword } from "utils/functions";
import { TableRowSelection } from "antd/es/table/interface";
import { AlertRuleGroupRecipients } from "api/interfaces/alertInterface.interface";
import {
  getHeaderCheckboxState,
  createCellToggleHandler,
  createHeaderToggleHandler,
} from "utils/UserUtil";
import {
  SearchVirtualTable,
  SearchVirtualTableRecord,
} from "./SearchVirtualTable";

export type SearchUserGroupProps = {
  accountId: string;
  keyword?: string;
  selectedUserGroupId?: string[];
  onSelectedRowsChangeUserGroup?: (selectedRows: string[]) => void;
  onChangeEnableCheckUserGroup?: (
    selectedRows: AlertRuleGroupRecipients[]
  ) => void;

  isSupportEmail?: boolean;
  enableCheckUserGroupList?: AlertRuleGroupRecipients[];
  previlege?: string;
};

interface DataRow extends SearchVirtualTableRecord {
  id: string;
  name: string;
  description: string;
}

export const SearchUserGroup = forwardRef(
  (props: SearchUserGroupProps, ref: ForwardedRef<any>): JSX.Element => {
    // console.log(props.enableCheckUserGroupList);
    const [groups, setGroups] = useState<DataRow[]>([]);
    const [pending, setPending] = useState<boolean>(true);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

    const [groupQueryInfo] = useState<BasePagination>({
      total: 0,
      pageNum: 0,
      pageLimit: 2000,
      keyword: "",
      sortType: "name",
      sortDirection: SORT_DIRECTION.ASC,
    });

    const data: DataRow[] = useMemo(() => {
      return groups.filter((rec) => {
        if (props.keyword) {
          return (
            matchStringSearchKeyword(rec.name, props.keyword) ||
            matchStringSearchKeyword(rec.description, props.keyword)
          );
        } else return true;
      });
    }, [props.keyword, groups]);

    const selectedEnable = useMemo(() => {
      return (data ?? []).map((group: DataRow) => {
        return {
          userGroupId: group.id,
          enableEmail:
            props.enableCheckUserGroupList?.find(
              (item) => item.userGroupId === group.id
            )?.enableEmail || false,
          enablePush:
            props.enableCheckUserGroupList?.find(
              (item) => item.userGroupId === group.id
            )?.enablePush || false,
        };
      });
    }, [props.enableCheckUserGroupList, data]);

    const onCheckRecipient = useCallback(
      (recipients: AlertRuleGroupRecipients[]) => {
        setGroups((prev) =>
          prev.map((group) => {
            const recipient = recipients.find(
              (recipient) => recipient.userGroupId === group.id
            );
            if (recipient) {
              return {
                ...group,
                enableEmail: recipient.enableEmail,
                enablePush: recipient.enablePush,
              };
            }
            // recipient가 없으면 기존 값 유지
            return group;
          })
        );
        if (props.onChangeEnableCheckUserGroup) {
          // 기존 enableCheckUserGroupList와 병합하여 업데이트
          const existingRecipients = props.enableCheckUserGroupList || [];
          const updatedGroupIds = recipients.map((r) => r.userGroupId);
          const mergedRecipients = [
            ...existingRecipients.filter(
              (existing) => !updatedGroupIds.includes(existing.userGroupId)
            ),
            ...recipients,
          ];
          props.onChangeEnableCheckUserGroup(mergedRecipients);
        }
      },
      [props.onChangeEnableCheckUserGroup, props.enableCheckUserGroupList]
    );

    // 헤더 체크박스 상태 계산 공통 함수
    const getHeaderCheckboxStateMemo = useCallback(
      ({ field }: { field: "enableEmail" | "enablePush" }) => {
        return getHeaderCheckboxState({ selectedEnable, field });
      },
      [selectedEnable]
    );

    // 개별 체크박스 onChange 핸들러 공통 함수
    const handleCellToggle = useCallback(
      createCellToggleHandler({
        selectedEnable,
        onCheckRecipient,
        getId: (item) => item.userGroupId,
      }),
      [selectedEnable, onCheckRecipient]
    );

    // 헤더 토글 핸들러 공통 함수
    const handleHeaderToggle = useCallback(
      createHeaderToggleHandler({
        filteredData: data,
        allItems: groups,
        existingRecipients: props.enableCheckUserGroupList,
        onCheckRecipient,
        getId: (group) => group.id,
        getRecipientId: (recipient) => recipient.userGroupId,
        createRecipient: (userGroupId, enableEmail, enablePush) =>
          ({
            userGroupId,
            enableEmail,
            enablePush,
          } as AlertRuleGroupRecipients),
      }),
      [data, groups, props.enableCheckUserGroupList, onCheckRecipient]
    );

    useQuery(
      ["group", groupQueryInfo, props.previlege],
      () =>
        props.previlege
          ? getGroupPrevilegeGroupList({
              payload: groupQueryInfo,
              accountId: props.accountId,
              previlege: props.previlege,
            })
          : getGroupList({
              payload: groupQueryInfo,
              accountId: props.accountId,
            }),
      {
        retry: 0,
        refetchOnWindowFocus: false,
        onSuccess: (res: any) => {
          if (res.result === undefined) {
            return;
          }
          setGroups(
            res.result.map((group: any) => {
              return {
                ...group,
                id: group.groupId as string,
                key: group.groupId as string,
                name: group.name,
                description: group.description,
              };
            })
          );
        },
        onError: (e: any) => {},
        onSettled: () => {
          setPending(false);
        },
      }
    );

    const onSelectChange = (
      newSelectedRowKeys: React.Key[],
      selectedRows: DataRow[]
    ) => {
      setSelectedRowKeys(newSelectedRowKeys);
      if (props.onSelectedRowsChangeUserGroup)
        props.onSelectedRowsChangeUserGroup(newSelectedRowKeys as string[]);
    };

    const rowSelection: TableRowSelection<DataRow> = {
      columnWidth: 40,
      selectedRowKeys,
      onChange: onSelectChange,
    };

    useEffect(() => {
      // console.log(props.selectedUserGroupId, props.enableCheckUserGroupList);
      if (props.selectedUserGroupId !== undefined) {
        setSelectedRowKeys(props.selectedUserGroupId);
      }
    }, [props.selectedUserGroupId]);

    const columns: TableColumnsType<DataRow> = useMemo(
      () => [
        {
          key: "name",
          dataIndex: "name",
          title: "Name",
          render: (value, row) => (
            <>
              <b>{value}</b>
              <address className="location">{row.description}</address>
            </>
          ),
          ellipsis: true,
        },
        {
          key: "enableEmail",
          dataIndex: "enableEmail",
          title: ({ filters }) => {
            const { checked, indeterminate } = getHeaderCheckboxStateMemo({
              field: "enableEmail",
            });
            return (
              <div>
                <Checkbox
                  defaultChecked={false}
                  checked={checked}
                  indeterminate={indeterminate}
                  onChange={(e) => {
                    handleHeaderToggle({
                      field: "enableEmail",
                      checked: e.target.checked,
                    });
                  }}
                />
                Email
              </div>
            );
          },
          render: (value, row, index) => (
            <Checkbox
              checked={selectedEnable.some(
                (item) => item.userGroupId === row.id && item.enableEmail
              )}
              onChange={() =>
                handleCellToggle({ field: "enableEmail", id: row.id })
              }
            />
          ),
          width: 80,
          hidden: !!!props.isSupportEmail,
        },
        {
          key: "enablePush",
          dataIndex: "enablePush",
          title: ({ filters }) => {
            const { checked, indeterminate } = getHeaderCheckboxStateMemo({
              field: "enablePush",
            });
            return (
              <div>
                <Checkbox
                  defaultChecked={false}
                  checked={checked}
                  indeterminate={indeterminate}
                  onChange={(e) => {
                    handleHeaderToggle({
                      field: "enablePush",
                      checked: e.target.checked,
                    });
                  }}
                />
                Push
              </div>
            );
          },
          render: (value, row, index) => (
            <Checkbox
              checked={selectedEnable.some(
                (item) => item.userGroupId === row.id && item.enablePush
              )}
              onChange={() =>
                handleCellToggle({ field: "enablePush", id: row.id })
              }
            />
          ),
          width: 80,
          hidden: !!!props.isSupportEmail,
        },
      ],
      [
        selectedEnable,
        getHeaderCheckboxStateMemo,
        handleCellToggle,
        handleHeaderToggle,
        props.isSupportEmail,
      ]
    );

    return (
      <SearchVirtualTable<DataRow>
        columns={columns}
        dataSource={data}
        loading={pending}
        isSingleMode={false}
        useExpandable={false}
        useRowExpandedSelection={false}
        selectedRowKeys={!props.isSupportEmail ? selectedRowKeys : []}
        onSelectChange={
          !props.isSupportEmail
            ? (keys, rows) =>
                onSelectChange(keys as string[], rows as DataRow[])
            : undefined
        }
        checkDisabled={() => props.isSupportEmail ?? false }
      />
    );
  }
);
